import { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
} from '@mui/material';
import BrandSelect from "./BrandSelect";
import { Brand } from "../../types/paymentProcessor";
import { useApi } from "../../hooks/useApi";
import RepresentmentStartDateField from "./RepresentmentStartDateField";
import { useRepresentmentStartDate } from "../../hooks/useRepresentmentStartDate";

interface StripeDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  apiUrl: string;
  showRepresentmentStartDate?: boolean;
  brands: Brand[];
  refreshBrands: () => void;
  createBrandUrl?: string;
}

export default function StripeDialog({
  open,
  onClose,
  onSuccess,
  apiUrl,
  showRepresentmentStartDate,
  brands,
  refreshBrands,
  createBrandUrl,
}: StripeDialogProps) {
  const { post } = useApi();
  const [formData, setFormData] = useState({ name: '', secret: '' });
  const [selectedBrandId, setSelectedBrandId] = useState<number | ''>('');
  const representmentStartDate = useRepresentmentStartDate();
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const resetAndClose = () => {
    setFormData({ name: '', secret: '' });
    setSelectedBrandId('');
    representmentStartDate.reset();
    setFormError(null);
    onClose();
  };

  const handleDialogClose = (_event: object, reason: string) => {
    if (reason !== 'backdropClick') {
      resetAndClose();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setFormError(null);
  };

  const handleCreate = async () => {
    setFormError(null);
    setSubmitting(true);

    try {
      await post(apiUrl, {
        name: formData.name,
        secret: formData.secret,
        brandId: selectedBrandId,
        ...(showRepresentmentStartDate ? representmentStartDate.payload : {}),
      });

      resetAndClose();
      await onSuccess();
    } catch (err: any) {
      setFormError(err.message || 'Failed to link Stripe account');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth="sm" fullWidth>
      <DialogTitle>Link Stripe Account</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2, background: '#f5f5f7', p: 2, borderRadius: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 500, mb: 0.5 }}>
            Stripe API Key Requirements
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            To link a Stripe account, please provide a private (restricted) API key with the following permissions:
          </Typography>
          <ul style={{ margin: '8px 0 0 20px', color: 'rgba(0, 0, 0, 0.8)', padding: 0 }}>
          <li>
              <Typography variant="body2" component="span">
                <strong>Charges and Refunds:</strong> write permission
              </Typography>
            </li>
            <li>
              <Typography variant="body2" component="span">
                <strong>Connect:</strong> read permission
              </Typography>
            </li>
            <li>
              <Typography variant="body2" component="span">
                <strong>Disputes:</strong> write permission
              </Typography>
            </li>
            <li>
              <Typography variant="body2" component="span">
                <strong>Webhook Endpoints:</strong> write permission
              </Typography>
            </li>
          </ul>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            <strong>Important:</strong> When creating restricted key in the Stripe dashboard, select <strong>"Building your own integration"</strong> when asked "How will you use this API key?"&nbsp; Do <strong>not</strong> select "Providing this key to another website".
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            To submit evidence from the portal the key also needs Disputes: write and Files: write. Save the key again after changing its permissions.
          </Typography>
        </Box>

        {formError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {formError}
          </Alert>
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            label="Name"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            fullWidth
            required
            disabled={submitting}
            size="small"
          />
          <TextField
            label="Secret"
            name="secret"
            type="password"
            value={formData.secret}
            onChange={handleInputChange}
            fullWidth
            required
            disabled={submitting}
            size="small"
          />
          <BrandSelect
            brands={brands}
            value={selectedBrandId}
            onChange={(brandId) => setSelectedBrandId(brandId)}
            refreshBrands={refreshBrands}
            disabled={submitting}
            createBrandUrl={createBrandUrl}
          />
          {showRepresentmentStartDate && (
            <RepresentmentStartDateField
              value={representmentStartDate.value}
              onChange={representmentStartDate.setValue}
              disabled={submitting}
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={resetAndClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={handleCreate}
          variant="contained"
          disabled={submitting || !formData.name || !formData.secret || !selectedBrandId || (showRepresentmentStartDate && representmentStartDate.invalid)}
          sx={{
            backgroundColor: '#465fff',
            '&:hover': {
              backgroundColor: '#3641f5',
            },
          }}
        >
          {submitting ? 'Creating...' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
